import React from 'react';
import MainLayout from '../layouts/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from '@inertiajs/react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { FiCalendar, FiHome, FiUser, FiBarChart2, FiBell, FiDollarSign } from 'react-icons/fi';

export default function FacilityOwnerDashboard({ user, facilities = [], stats = {}, subscription = null, recent_leads = [], upcoming_tours = [] }) {
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    }).format(date);
  };

  return (
    <MainLayout user={user}>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Welcome banner */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg shadow-lg mb-6 p-6 text-white">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">Welcome back, {user.email}</h1>
                <p className="mt-1 text-blue-100">Here's what's happening with your facilities today.</p>
              </div>
              <div className="mt-4 md:mt-0 flex space-x-3">
                <Button className="bg-white text-blue-700 hover:bg-blue-50" asChild>
                  <Link href="/facility_owner/facilities/new">Add New Facility</Link>
                </Button>
                <Button variant="outline" className="border-white text-white hover:bg-blue-700" asChild>
                  <Link href="/facility_owner/profile">Manage Profile</Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Key stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
            <Card className="bg-white shadow-sm hover:shadow transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-blue-100 mr-4">
                    <FiHome className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Total Facilities</p>
                    <h3 className="text-2xl font-bold text-gray-900">{stats.total_facilities || 0}</h3>
                  </div>
                </div>
                <div className="mt-4 flex justify-between items-center">
                  <Progress value={stats.total_facilities > 0 ? 100 : 0} className="h-1 flex-grow mr-4" />
                  <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-800 p-0" asChild>
                    <Link href="/facility_owner/facilities">View All</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-sm hover:shadow transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-indigo-100 mr-4">
                    <FiHome className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Total Units</p>
                    <h3 className="text-2xl font-bold text-gray-900">{stats.total_units || 0}</h3>
                  </div>
                </div>
                <div className="mt-4 flex justify-between items-center">
                  <Progress value={stats.total_units > 0 ? 100 : 0} className="h-1 flex-grow mr-4" />
                  <Button variant="ghost" size="sm" className="text-indigo-600 hover:text-indigo-800 p-0" asChild>
                    <Link href="/facility_owner/facilities">Manage in Facilities</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-sm hover:shadow transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-green-100 mr-4">
                    <FiUser className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Total Leads</p>
                    <h3 className="text-2xl font-bold text-gray-900">{stats.total_leads || 0}</h3>
                  </div>
                </div>
                <div className="mt-4 flex justify-between items-center">
                  <Progress value={(stats.total_leads || 0) > 0 ? 100 : 0} className="h-1 flex-grow mr-4" />
                  <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-800 p-0" asChild>
                    <Link href="/facility_owner/leads">View All</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-sm hover:shadow transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-purple-100 mr-4">
                    <FiCalendar className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Scheduled Tours</p>
                    <h3 className="text-2xl font-bold text-gray-900">{stats.upcoming_tours || 0}</h3>
                  </div>
                </div>
                <div className="mt-4 flex justify-between items-center">
                  <Progress value={(stats.upcoming_tours || 0) > 0 ? 100 : 0} className="h-1 flex-grow mr-4" />
                  <Button variant="ghost" size="sm" className="text-purple-600 hover:text-purple-800 p-0" asChild>
                    <Link href="/facility_owner/tours">View All</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-sm hover:shadow transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 rounded-full bg-amber-100 mr-4">
                    <FiBarChart2 className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Occupancy Rate</p>
                    <h3 className="text-2xl font-bold text-gray-900">{stats.occupancy_rate || 0}%</h3>
                  </div>
                </div>
                <div className="mt-4 flex justify-between items-center">
                  <Progress value={stats.occupancy_rate || 0} className="h-1 flex-grow mr-4" />
                  <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-800 p-0" asChild>
                    <Link href="/facility_owner/analytics">View Analytics</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main content area */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column - Facilities */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="shadow-sm">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle>My Facilities</CardTitle>
                    <Button variant="outline" size="sm" asChild>
                      <Link href="/facility_owner/facilities">View All</Link>
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {facilities.length > 0 ? (
                    <div className="space-y-4">
                      {facilities.map((facility) => (
                        <div key={facility.id} className="p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h3 className="font-medium text-lg">{facility.name}</h3>
                              <p className="text-sm text-gray-500">Last updated: {formatDate(facility.updated_at)}</p>
                            </div>
                            <Badge variant={facility.published ? "success" : "secondary"}>
                              {facility.published ? "Published" : "Draft"}
                            </Badge>
                          </div>

                          <div className="grid grid-cols-3 gap-4 mt-4">
                            <div className="text-center p-2 bg-gray-50 rounded">
                              <p className="text-sm text-gray-500">Leads</p>
                              <p className="font-bold">{facility.leads_count}</p>
                            </div>
                            <div className="text-center p-2 bg-gray-50 rounded">
                              <p className="text-sm text-gray-500">Tours</p>
                              <p className="font-bold">{facility.tours_count}</p>
                            </div>
                            <div className="text-center p-2 bg-gray-50 rounded">
                              <p className="text-sm text-gray-500">Occupancy</p>
                              <p className="font-bold">{facility.occupancy_percentage}%</p>
                            </div>
                          </div>

                          <div className="mt-4 flex justify-end">
                            <Button variant="outline" size="sm" className="mr-2" asChild>
                              <Link href={`/facilities/${facility.slug}`}>View Listing</Link>
                            </Button>
                            <Button size="sm" asChild>
                              <Link href={`/facility_owner/facilities/${facility.id}`}>Manage</Link>
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <FiHome className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-1">No facilities yet</h3>
                      <p className="text-gray-500 mb-4">Get started by adding your first facility.</p>
                      <Button asChild>
                        <Link href="/facility_owner/facilities/new">Add New Facility</Link>
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Tabs defaultValue="leads" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="leads">Recent Leads</TabsTrigger>
                  <TabsTrigger value="tours">Upcoming Tours</TabsTrigger>
                </TabsList>

                <TabsContent value="leads" className="mt-4">
                  <Card>
                    <CardContent className="p-4">
                      {recent_leads && recent_leads.length > 0 ? (
                        <div className="divide-y">
                          {recent_leads.map((lead) => (
                            <div key={lead.id} className="py-3 flex justify-between items-center">
                              <div>
                                <p className="font-medium">{lead.user ? lead.user.email : 'Anonymous Lead'}</p>
                                <p className="text-sm text-gray-500">For: {lead.facility.name}</p>
                                <div className="flex items-center mt-1">
                                  <Badge variant="outline" className="text-xs">{lead.source || 'website'}</Badge>
                                  <span className="text-xs text-gray-500 ml-2">{formatDate(lead.created_at)}</span>
                                </div>
                              </div>
                              <Button variant="outline" size="sm" asChild>
                                <Link href={`/facility_owner/leads/${lead.id}`}>View</Link>
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-gray-500">No recent leads.</p>
                        </div>
                      )}
                    </CardContent>
                    <CardFooter className="bg-gray-50 px-4 py-3">
                      <Button variant="ghost" size="sm" className="w-full" asChild>
                        <Link href="/facility_owner/leads">View All Leads</Link>
                      </Button>
                    </CardFooter>
                  </Card>
                </TabsContent>

                <TabsContent value="tours" className="mt-4">
                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <div>
                          <CardTitle>Upcoming Tours</CardTitle>
                          <CardDescription>Scheduled facility visits</CardDescription>
                        </div>
                        <Button variant="outline" size="sm" asChild>
                          <Link href="/facility_owner/tours">
                            View All
                          </Link>
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="p-4">
                      {upcoming_tours && upcoming_tours.length > 0 ? (
                        <div className="divide-y">
                          {upcoming_tours.map((tour) => (
                            <div key={tour.id} className="py-3">
                              <div className="flex justify-between items-start">
                                <div className="font-medium">{formatDate(tour.scheduled_at)}</div>
                                <Badge variant={{
                                  pending: 'secondary',
                                  confirmed: 'success',
                                  completed: 'default',
                                  cancelled: 'destructive'
                                }[tour.status] || 'secondary'}>
                                  {tour.status}
                                </Badge>
                              </div>
                              <div className="flex justify-between items-center mt-1">
                                <div className="text-sm text-gray-500">
                                  <FiHome className="inline-block mr-1 h-3 w-3" />
                                  {tour.facility?.name || 'Unknown facility'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  <FiCalendar className="inline-block mr-1 h-3 w-3" />
                                  {formatTime(tour.scheduled_at)}
                                </div>
                              </div>
                              <div className="mt-2 flex justify-end">
                                <Button variant="ghost" size="sm" className="h-7 px-2" asChild>
                                  <Link href={`/facility_owner/tours/${tour.id}`}>View Details</Link>
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 px-2" asChild>
                                  <Link href={`/facility_owner/facilities/${tour.facility_id}`}>View Facility</Link>
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-6 text-center">
                          <p className="text-gray-500">No upcoming tours scheduled</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>

            {/* Right column - Subscription & Quick actions */}
            <div className="space-y-6">
              <Card className="shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4">
                  <CardTitle className="text-white">Subscription</CardTitle>
                </div>
                <CardContent className="p-6">
                  {subscription ? (
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-medium text-gray-900">Current Plan</h3>
                        <p className="text-2xl font-bold text-blue-600">{subscription.name}</p>
                        <p className="text-sm text-gray-500 mt-1">Expires: {formatDate(subscription.expires_at)}</p>
                      </div>

                      {subscription.features && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-500 mb-2">Features</h4>
                          <ul className="space-y-1">
                            {subscription.features.map((feature, index) => (
                              <li key={index} className="flex items-center text-sm">
                                <svg className="h-4 w-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <Button className="w-full" asChild>
                        <Link href="/facility_owner/subscription">Manage Subscription</Link>
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <FiDollarSign className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-1">No active subscription</h3>
                      <p className="text-gray-500 mb-4">Subscribe to unlock premium features.</p>
                      <Button asChild>
                        <Link href="/facility_owner/subscription/new">Choose a Plan</Link>
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-3">
                    <Button variant="outline" className="w-full justify-start" asChild>
                      <Link href="/facility_owner/facilities/new">
                        <FiHome className="h-5 w-5 mr-2" />
                        Add New Facility
                      </Link>
                    </Button>
                    <Button variant="outline" className="w-full justify-start" asChild>
                      <Link href="/facility_owner/tour_slots">
                        <FiCalendar className="h-5 w-5 mr-2" />
                        Manage Tour Slots
                      </Link>
                    </Button>
                    <Button variant="outline" className="w-full justify-start" asChild>
                      <Link href="/facility_owner/leads">
                        <FiUser className="h-5 w-5 mr-2" />
                        View All Leads
                      </Link>
                    </Button>
                    <Button variant="outline" className="w-full justify-start" asChild>
                      <Link href="/facility_owner/profile">
                        <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Edit Profile
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-sm bg-gray-50">
                <CardHeader className="pb-2">
                  <CardTitle>Units Overview</CardTitle>
                  <CardDescription>Current availability</CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium">Total Units</span>
                        <span className="text-sm font-medium">{stats.total_units || 0}</span>
                      </div>
                      <Progress value={100} className="h-2" />
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium">Available Units</span>
                        <span className="text-sm font-medium">{stats.total_units_available || 0}</span>
                      </div>
                      <Progress
                        value={stats.total_units > 0 ? (stats.total_units_available / stats.total_units) * 100 : 0}
                        className="h-2"
                      />
                    </div>
                    <div className="pt-2">
                      <Button variant="outline" size="sm" className="w-full" asChild>
                        <Link href="/facility_owner/units">Manage Units</Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
